/* eslint-disable jsx-a11y/no-autofocus --
 * The single autoFocus in this file moves focus into a control the user just
 * summoned (an inline editor that appears on their own click, or a modal's
 * first field). That is required focus management: leaving focus behind
 * strands keyboard users on an element that has since vanished. The rule
 * guards against autofocus on PAGE LOAD, which is a different thing and is
 * not done anywhere here. File-scoped rather than line-scoped because
 * Prettier reformats JSX props across lines, which silently detaches a
 * line-targeted disable from what it was suppressing.
 */
import {
  CheckOutlined,
  CloseOutlined,
  ControlOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  InboxOutlined,
  PlusOutlined,
  SyncOutlined,
  UpOutlined,
} from '@ant-design/icons';
// Switch dropped 2026-06-30 — only used by the now-commented mandatory toggle.
import { Button, Drawer, Input, Spin, Tooltip, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type FileEntry,
  type FileEntryCondition,
  type WorkflowStep,
  workflowsApi,
} from '@/api/workflows';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { ConditionsEditorModal } from './conditions/ConditionsEditorModal';
import { fileLabel } from './entries/useEntryUpload';
import { extractApiError } from './statusUtils';

const { Text } = Typography;
const { Dragger } = Upload;

const I18N_NS = 'workflow.steps.fileEntries';
const TINT_FG = '#0179C1';

// Slightly smaller than the global 32x32 icon button — these sit in a tight
// header row inside each entry card.
const iconBtn = {
  ...ICON_BTN_PROPS,
  style: { ...ICON_BTN_PROPS.style, width: 26, height: 26, fontSize: 12, borderRadius: 6 },
} as const;

type Language = 'en' | 'fr';

type Props = {
  templateId: string;
  step: WorkflowStep | null;
  /** All steps in the workflow — passed in so the per-PDF condition modal can
   *  enumerate selectable source fields across every DynamicForm step. */
  allSteps: WorkflowStep[];
  editable: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
};

const isPdfFile = (file: { type: string }) => file.type === 'application/pdf';

/**
 * Force every label rendered or saved in this drawer to end with `.pdf`.
 * Covers two cases: legacy rows that stored stripped names, and originator
 * renames that drop the suffix.
 */
function ensurePdfSuffix(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  if (/\.pdf$/i.test(trimmed)) return trimmed;
  return `${trimmed}.pdf`;
}

/** True when the entry carries at least one gating condition. */
function hasCondition(entry: FileEntry): boolean {
  return (entry.conditions?.length ?? 0) > 0;
}

/** Immutable Set toggle — keeps both id-sets (expanded, mandatory) one-line. */
function toggleInSet(prev: Set<string>, id: string): Set<string> {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export function PdfEntriesDrawer({
  templateId,
  step,
  allSteps,
  editable,
  onClose,
  onChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  // Condition modal target — null = closed. Sits at the drawer root so one
  // instance serves every card.
  const [conditionTarget, setConditionTarget] = useState<FileEntry | null>(null);
  // Which entries are expanded. New entries default to expanded so the
  // originator lands on the upload zone right after clicking Add PDF.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Local-only "mandatory" toggle state per entry. Doesn't persist yet — the
  // BE field hasn't been added. Resets when the drawer reopens.
  const [mandatoryIds, setMandatoryIds] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);

  if (!step) return null;

  const stepTitle = (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr;
  const entries = step.fileEntries ?? [];

  const toggleExpanded = (id: string) => setExpandedIds((prev) => toggleInSet(prev, id));
  const toggleMandatory = (id: string) => setMandatoryIds((prev) => toggleInSet(prev, id));

  const handleAddPdf = async () => {
    setCreating(true);
    try {
      // Create a label-only entry; the originator uploads the actual EN / FR
      // files into the card's dropzones. Label is localized — "PDF Name.pdf"
      // / "Nom du PDF.pdf" so French originators don't see English placeholder.
      const created = await workflowsApi.addFileEntry(templateId, step.id, {
        label: t(`${I18N_NS}.defaultLabel`),
        enFileKey: null,
        frFileKey: null,
      });
      setExpandedIds((prev) => new Set(prev).add(created.id));
      await onChanged();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.addFailed`));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Drawer open={!!step} onClose={onClose} title={stepTitle} width={720} destroyOnHidden>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={creating}
          disabled={!editable}
          onClick={handleAddPdf}
          style={{ alignSelf: 'flex-start', borderRadius: 999, padding: '0 18px' }}
        >
          {t(`${I18N_NS}.addPdf`)}
        </Button>

        {entries.length === 0 && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t(`${I18N_NS}.empty`)}
          </Text>
        )}

        {entries.map((entry) => (
          <EntryCard
            key={entry.id}
            templateId={templateId}
            stepId={step.id}
            entry={entry}
            editable={editable}
            isExpanded={expandedIds.has(entry.id)}
            isMandatory={mandatoryIds.has(entry.id)}
            onToggleExpanded={() => toggleExpanded(entry.id)}
            onToggleMandatory={() => toggleMandatory(entry.id)}
            onConfigureCondition={setConditionTarget}
            onChanged={onChanged}
          />
        ))}
      </div>

      <ConditionModal
        templateId={templateId}
        stepId={step.id}
        entry={conditionTarget}
        allSteps={allSteps}
        onClose={() => setConditionTarget(null)}
        onSaved={async () => {
          setConditionTarget(null);
          await onChanged();
        }}
      />
    </Drawer>
  );
}

/**
 * One PDF entry. Collapses into a single row (name + actions + EN/FR file
 * summary); expands into a detail panel with separate EN + FR upload zones.
 * Conditions / delete / mandatory live in the header so they apply to the
 * EN + FR pair, which is exactly what the originator wanted — no more
 * setting the same condition twice for the EN and FR halves of one document.
 */
function EntryCard({
  templateId,
  stepId,
  entry,
  editable,
  isExpanded,
  // isMandatory / onToggleMandatory props are still wired from the parent
  // (mandatoryIds Set in PdfEntriesDrawer) but the switch that consumed them
  // is commented out below until StepFileEntry.IsMandatory lands on the BE.
  // Prefixing with _ to satisfy the unused-arg lint rule without dropping
  // the wiring — restoring the toggle is a single underscore removal.
  isMandatory: _isMandatory,
  onToggleExpanded,
  onToggleMandatory: _onToggleMandatory,
  onConfigureCondition,
  onChanged,
}: {
  templateId: string;
  stepId: string;
  entry: FileEntry;
  editable: boolean;
  isExpanded: boolean;
  isMandatory: boolean;
  onToggleExpanded: () => void;
  onToggleMandatory: () => void;
  onConfigureCondition: (entry: FileEntry) => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => ensurePdfSuffix(entry.label));
  const [saving, setSaving] = useState(false);

  const commitRename = async () => {
    const next = ensurePdfSuffix(draft);
    if (!next || next === ensurePdfSuffix(entry.label)) {
      setEditing(false);
      setDraft(ensurePdfSuffix(entry.label));
      return;
    }
    setSaving(true);
    try {
      await workflowsApi.editFileEntry(templateId, stepId, entry.id, {
        label: next,
        enFileKey: entry.enFileKey ?? null,
        frFileKey: entry.frFileKey ?? null,
      });
      await onChanged();
      setEditing(false);
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await workflowsApi.deleteFileEntry(templateId, stepId, entry.id);
      await onChanged();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.deleteFailed`));
    }
  };

  // Apply a new file key (or null to clear) without touching the other key
  // or the label. Used by the per-card EN/FR dropzones.
  const setFileKey = async (language: Language, key: string | null) => {
    await workflowsApi.editFileEntry(templateId, stepId, entry.id, {
      label: ensurePdfSuffix(entry.label),
      enFileKey: language === 'en' ? key : (entry.enFileKey ?? null),
      frFileKey: language === 'fr' ? key : (entry.frFileKey ?? null),
    });
    await onChanged();
  };

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header row — name + actions. Always visible. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          background: '#f8fafc',
          borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {editing ? (
            <Input
              autoFocus
              size="small"
              value={draft}
              disabled={saving}
              maxLength={120}
              onChange={(e) => setDraft(e.target.value)}
              onPressEnter={commitRename}
              style={{ maxWidth: 320 }}
            />
          ) : (
            <Text strong style={{ fontSize: 14, color: '#111827' }}>
              {ensurePdfSuffix(entry.label)}
            </Text>
          )}
          {!editing && (
            <Tooltip title={t(`${I18N_NS}.rename`)}>
              <Button
                {...iconBtn}
                icon={<EditOutlined />}
                disabled={!editable}
                onClick={() => setEditing(true)}
              />
            </Tooltip>
          )}
          {editing && (
            <>
              <Tooltip title={t('common.save')}>
                <Button
                  {...iconBtn}
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={saving}
                  onClick={commitRename}
                />
              </Tooltip>
              <Tooltip title={t('common.cancel')}>
                <Button
                  {...iconBtn}
                  icon={<CloseOutlined />}
                  disabled={saving}
                  onClick={() => {
                    setEditing(false);
                    setDraft(ensurePdfSuffix(entry.label));
                  }}
                />
              </Tooltip>
            </>
          )}
          {hasCondition(entry) && !editing && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t(`${I18N_NS}.conditionallyN`, { count: entry.conditions.length })}
            </Text>
          )}
        </div>

        <Tooltip
          title={t(hasCondition(entry) ? `${I18N_NS}.conditionEdit` : `${I18N_NS}.conditionAdd`)}
        >
          <Button
            {...iconBtn}
            icon={<ControlOutlined style={{ color: hasCondition(entry) ? TINT_FG : undefined }} />}
            disabled={!editable}
            onClick={() => onConfigureCondition(entry)}
          />
        </Tooltip>

        <ConfirmPopover
          title={t(`${I18N_NS}.deleteConfirmTitle`)}
          description={t(`${I18N_NS}.deleteConfirmDesc`)}
          onConfirm={handleDelete}
          disabled={!editable}
        >
          <Tooltip title={t(`${I18N_NS}.delete`)}>
            <Button {...iconBtn} danger icon={<DeleteOutlined />} disabled={!editable} />
          </Tooltip>
        </ConfirmPopover>

        {/* Mandatory toggle hidden 2026-06-30 — state is local-only (mandatoryIds
            Set in PdfEntriesDrawer), nothing persists to the BE yet. Bringing
            it back means landing `StepFileEntry.IsMandatory` on the domain +
            DDB + wire shape. Leaving the wiring intact (state, props, i18n key)
            so the eventual real persistence flip is a single comment removal.
        <Tooltip title={t(`${I18N_NS}.mandatoryTooltip`)}>
          <Switch
            size="small"
            checked={isMandatory}
            onChange={onToggleMandatory}
            disabled={!editable}
          />
        </Tooltip>
        */}

        <Tooltip title={t(isExpanded ? `${I18N_NS}.collapse` : `${I18N_NS}.expand`)}>
          <Button
            {...iconBtn}
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={onToggleExpanded}
          />
        </Tooltip>
      </div>

      {/* Collapsed body — compact summary line per language. */}
      {!isExpanded && (entry.enFileKey || entry.frFileKey) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            padding: '10px 14px',
            flexWrap: 'wrap',
          }}
        >
          <FileSummary
            language="en"
            fileKey={entry.enFileKey ?? null}
            uploadedAt={entry.enUploadedAt ?? null}
            templateId={templateId}
          />
          <FileSummary
            language="fr"
            fileKey={entry.frFileKey ?? null}
            uploadedAt={entry.frUploadedAt ?? null}
            templateId={templateId}
          />
        </div>
      )}

      {/* Expanded body — EN + FR upload zones with each language's current
          file shown above its dropzone for replace/remove. */}
      {isExpanded && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <LanguageSlot
            templateId={templateId}
            language="en"
            currentKey={entry.enFileKey ?? null}
            uploadedAt={entry.enUploadedAt ?? null}
            editable={editable}
            onSet={(key) => setFileKey('en', key)}
          />
          <LanguageSlot
            templateId={templateId}
            language="fr"
            currentKey={entry.frFileKey ?? null}
            uploadedAt={entry.frUploadedAt ?? null}
            editable={editable}
            onSet={(key) => setFileKey('fr', key)}
          />
        </div>
      )}
    </div>
  );
}

/** Per-language pill shown in the collapsed card body. */
function FileSummary({
  language,
  fileKey,
  uploadedAt,
  templateId,
}: {
  language: Language;
  fileKey: string | null;
  uploadedAt: string | null;
  templateId: string;
}) {
  const { t, i18n } = useTranslation();
  const label = language === 'en' ? 'EN' : 'FR';
  // Short, locale-aware date for the tight chip (e.g. "Jun 24"); full
  // date+time goes into the tooltip so the originator can hover for the
  // precise moment without crowding the pill.
  const shortDate = formatUploadDate(uploadedAt, i18n.language, 'short');
  const fullDate = formatUploadDate(uploadedAt, i18n.language, 'full');

  const handlePreview = async () => {
    if (!fileKey) return;
    try {
      const url = await workflowsApi.createDownloadUrl(templateId, fileKey);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.previewFailed`));
    }
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 0,
        // Soft chip background so each language summary reads as a single
        // grouped unit instead of a loose run of inline elements.
        padding: '6px 12px',
        background: fileKey ? '#f8fafc' : '#fafafa',
        border: '1px solid #e5e7eb',
        borderRadius: 999,
      }}
    >
      {/* Bold EN: / FR: prefix in BMO blue so the language is the visual
          anchor of each pill, per the originator's note. */}
      <Text
        strong
        style={{
          fontSize: 12,
          color: TINT_FG,
          letterSpacing: 0.4,
        }}
      >
        {label}:
      </Text>
      {fileKey ? (
        <>
          <FilePdfOutlined style={{ color: '#DC2626', fontSize: 16 }} />
          <Text style={{ fontSize: 13, color: '#111827' }} ellipsis>
            {filenameFromKey(fileKey)}
          </Text>
          {shortDate && (
            <Tooltip title={t(`${I18N_NS}.uploadedAt`, { date: fullDate })}>
              <Text style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
                · {shortDate}
              </Text>
            </Tooltip>
          )}
          <Tooltip title={t(`${I18N_NS}.preview`)}>
            <Button
              {...iconBtn}
              icon={<EyeOutlined />}
              onClick={handlePreview}
              style={{ ...iconBtn.style, width: 22, height: 22, background: 'transparent' }}
            />
          </Tooltip>
        </>
      ) : (
        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
          {t(`${I18N_NS}.noFile`)}
        </Text>
      )}
    </div>
  );
}

/**
 * Format an upload timestamp for display. Returns:
 *  - `short` — locale-aware month + day, e.g. "Jun 24" / "24 juin", for the
 *    tight collapsed chip.
 *  - `full` — locale-aware long form with time, e.g. "June 24, 2026 at 10:14 AM"
 *    / "24 juin 2026 à 10:14", for the expanded slot subtext + chip tooltip.
 *
 * Returns `''` on null/empty/unparseable input so callers can fall back
 * cleanly.
 */
function formatUploadDate(
  iso: string | null | undefined,
  locale: string,
  mode: 'short' | 'full',
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // Normalize "en-US-x-..." etc down to the language tag Intl understands
  // without throwing on edge-case region tags.
  const lang = locale.startsWith('fr') ? 'fr-CA' : 'en-CA';
  if (mode === 'short') {
    return new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }).format(date);
  }
  return new Intl.DateTimeFormat(lang, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * EN or FR upload slot inside an expanded card. Shows the current file (with
 * a remove ✕) when present; the Dragger sits below to either add the first
 * file or replace the existing one. Upload runs via the shared presigned-PUT
 * flow, then PATCHes the entry to set the language-specific key.
 */
function LanguageSlot({
  templateId,
  language,
  currentKey,
  uploadedAt,
  editable,
  onSet,
}: {
  templateId: string;
  language: Language;
  currentKey: string | null;
  uploadedAt: string | null;
  editable: boolean;
  onSet: (key: string | null) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const fullDate = formatUploadDate(uploadedAt, i18n.language, 'full');
  const [uploading, setUploading] = useState(false);
  const headerKey = language === 'en' ? 'enHeader' : 'frHeader';
  const addKey = language === 'en' ? 'addEn' : 'addFr';

  const uploadOne = async (file: RcFile) => {
    // Sign and send the SAME content type — SigV4 presigns Content-Type into
    // SignedHeaders, so a divergence here is a 403 SignatureDoesNotMatch from S3.
    // Fallback matches the BE default for a blank type (S3PdfTemplateStorage).
    const contentType = file.type || 'application/pdf';
    const { uploadUrl, key } = await workflowsApi.createUploadUrl(templateId, {
      filename: file.name,
      contentType,
      contentLength: file.size,
    });
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': contentType },
      transformRequest: [(d) => d],
      withCredentials: false,
    });
    await onSet(key);
  };

  const beforeUpload = (file: RcFile) => {
    if (!isPdfFile(file)) {
      message.error(t(`${I18N_NS}.onlyPdf`));
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    void (async () => {
      try {
        await uploadOne(file);
        message.success(t(`${I18N_NS}.uploadedN`, { count: 1 }));
      } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 400) {
          const data = axios.isAxiosError(err) ? err.response?.data : undefined;
          const maxBytes = (data as { maxUploadBytes?: number } | undefined)?.maxUploadBytes;
          message.error(
            maxBytes
              ? t(`${I18N_NS}.tooLarge`, { mb: Math.round(maxBytes / (1024 * 1024)) })
              : (extractApiError(err).message ?? t(`${I18N_NS}.uploadFailed`)),
          );
        } else if (status === 429) {
          message.error(t(`${I18N_NS}.rateLimited`));
        } else {
          message.error(extractApiError(err).message ?? t(`${I18N_NS}.uploadFailed`));
        }
      } finally {
        setUploading(false);
      }
    })();
    return Upload.LIST_IGNORE;
  };

  const handleRemove = async () => {
    try {
      await onSet(null);
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  // Mint a fresh presigned GET URL and open in a new tab so the originator can
  // sanity-check what's stored before saving. The URL expires (BE-controlled),
  // so we open it on click rather than caching it.
  const handlePreview = async () => {
    if (!currentKey) return;
    try {
      const url = await workflowsApi.createDownloadUrl(templateId, currentKey);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.previewFailed`));
    }
  };

  return (
    <div>
      <Text strong style={{ fontSize: 13, color: '#111827', display: 'block', marginBottom: 8 }}>
        {t(`${I18N_NS}.${headerKey}`)}
      </Text>
      {currentKey ? (
        // A file is already attached. Each language slot holds at most one
        // PDF, so don't show another dropzone — surface Replace + Remove on
        // the file row instead. Replace re-uses the file picker via a hidden
        // Upload (no Dragger) so the originator can swap an updated version
        // without removing first.
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            background: '#fff',
          }}
        >
          <FilePdfOutlined style={{ color: '#DC2626', fontSize: 18 }} />
          {/* Filename + "Uploaded {date}" stacked so the timestamp reads as
              metadata on the file rather than competing with the action icons.
              Tooltip on the filename surfaces the full key (debug aid). */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Text style={{ fontSize: 13, color: '#111827' }} ellipsis>
              {filenameFromKey(currentKey)}
            </Text>
            {fullDate && (
              <Text style={{ fontSize: 11, color: '#6b7280' }}>
                {t(`${I18N_NS}.uploadedAt`, { date: fullDate })}
              </Text>
            )}
          </div>
          <Tooltip title={t(`${I18N_NS}.preview`)}>
            <Button {...iconBtn} icon={<EyeOutlined />} onClick={handlePreview} />
          </Tooltip>
          <Upload
            accept="application/pdf"
            beforeUpload={beforeUpload}
            showUploadList={false}
            multiple={false}
            disabled={!editable || uploading}
          >
            <Tooltip title={t(`${I18N_NS}.replaceFile`)}>
              <Button
                {...iconBtn}
                icon={<SyncOutlined spin={uploading} />}
                disabled={!editable || uploading}
              />
            </Tooltip>
          </Upload>
          <ConfirmPopover
            title={t(`${I18N_NS}.removeFileConfirmTitle`)}
            description={t(`${I18N_NS}.removeFileConfirmDesc`)}
            onConfirm={handleRemove}
            disabled={!editable}
          >
            <Tooltip title={t(`${I18N_NS}.removeFile`)}>
              <Button {...iconBtn} danger icon={<DeleteOutlined />} disabled={!editable} />
            </Tooltip>
          </ConfirmPopover>
        </div>
      ) : (
        // No file yet — show the dropzone.
        <Dragger
          accept="application/pdf"
          beforeUpload={beforeUpload}
          showUploadList={false}
          multiple={false}
          disabled={!editable || uploading}
          style={{ borderRadius: 10 }}
        >
          {uploading ? (
            <div style={{ padding: 12 }}>
              <Spin />
              <p style={{ marginTop: 8, color: '#6b7280', fontSize: 13 }}>
                {t(`${I18N_NS}.uploading`)}
              </p>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 28, color: TINT_FG, margin: 0 }}>
                <InboxOutlined />
              </p>
              <p style={{ margin: '6px 0 2px', fontSize: 14, fontWeight: 500, color: '#111827' }}>
                {t(`${I18N_NS}.${addKey}`)}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                {t(`${I18N_NS}.dropHint`)}
              </p>
            </>
          )}
        </Dragger>
      )}
    </div>
  );
}

/**
 * Extract a human-readable filename from a stored S3 key. The key usually
 * looks like `workflow-uploads/<uuid>/<filename>` — we just take the basename.
 */
function filenameFromKey(key: string): string {
  const slash = key.lastIndexOf('/');
  const base = slash >= 0 ? key.slice(slash + 1) : key;
  return fileLabel(base);
}

/**
 * Thin wrapper around the shared ConditionsEditorModal — same model the
 * field/section visibility editors use. Owns persistence for the PDF entry
 * case (POSTs to /file-entries/{id}/conditions).
 */
function ConditionModal({
  templateId,
  stepId,
  entry,
  allSteps,
  onClose,
  onSaved,
}: {
  templateId: string;
  stepId: string;
  entry: FileEntry | null;
  allSteps: WorkflowStep[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const isOpen = entry !== null;
  const handleSave = async (conditions: FileEntryCondition[], combinator: 'all' | 'any' | null) => {
    if (!entry) return;
    setSaving(true);
    try {
      await workflowsApi.setFileEntryConditions(templateId, stepId, entry.id, {
        conditions,
        combinator,
      });
      await onSaved();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.conditionSaveFailed`));
    } finally {
      setSaving(false);
    }
  };
  return (
    <ConditionsEditorModal
      open={isOpen}
      title={t(`${I18N_NS}.conditionModalTitle`)}
      intro={t(`${I18N_NS}.conditionIntro`)}
      initialConditions={entry?.conditions ?? []}
      initialCombinator={entry?.conditionCombinator ?? null}
      allSteps={allSteps}
      saving={saving}
      onCancel={onClose}
      onSave={handleSave}
    />
  );
}
